import React from "react";
import { QueryResult, useQuery } from "@apollo/client";
import formatDate from "date-fns/format";
import isSameDay from "date-fns/isSameDay";
import {
  GET_PAYEE_TRANSACTIONS_OVERVIEW,
  GetPayeeTransactionsOverviewData,
  GetPayeeTransactionsOverviewVars,
} from "common/graphql";
import { getOverviewPeriods } from "@app.automotus.io/components/routes/dashboard/utils";

/** How often the dashboard clock re-checks whether the calendar day has rolled over. */
const CLOCK_TICK_MS = 60_000;

/**
 * Decides whether a clock tick is worth committing to state.
 *
 * Returns `prev` unchanged when the candidate falls on the same calendar day, which React treats
 * as a no-op, so the dashboard re-renders on day rollover rather than once a minute. Consumers
 * format this down to `yyyy-MM-dd`, so a within-day change would be invisible anyway.
 */
export function nextClockValue(prev: Date, candidate: Date): Date {
  return isSameDay(prev, candidate) ? prev : candidate;
}

/**
 * The dashboard's "now".
 *
 * This was previously `React.useState(new Date())`, which froze the clock at mount. Every period
 * boundary derived from it stayed pinned to whenever the page happened to load, so a tab left open
 * across midnight kept querying the previous day's period indefinitely.
 *
 * The tick only commits a new value when the calendar *day* changes, returning the previous
 * reference otherwise -- which React treats as a no-op. Every consumer formats this down to
 * `yyyy-MM-dd`, so sub-day precision was never used, and day granularity keeps this from
 * re-rendering the whole dashboard once a minute for nothing.
 */
export function useDashboardClock(): Date {
  const [now, setNow] = React.useState(() => new Date());

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      setNow((prev) => nextClockValue(prev, new Date()));
    }, CLOCK_TICK_MS);

    return () => clearInterval(intervalId);
  }, []);

  return now;
}

/**
 * How often an open dashboard re-checks the scorecard aggregate.
 *
 * The batch that feeds it lands assignments at :49 past the hour, so the tile reflects a new batch
 * within a few minutes of it landing rather than at a precise moment. Five minutes keeps that lag
 * small without turning every parked dashboard into a steady stream of queries.
 */
export const OVERVIEW_POLL_INTERVAL_MS = 5 * 60_000;

/**
 * Apollo fetch policy for the scorecard overview aggregate.
 *
 * Exported so it is a single named decision rather than an inline string, since the default is
 * what caused AUT-5782.
 */
export const OVERVIEW_FETCH_POLICY = "cache-and-network" as const;

/**
 * Runs the scorecard's overview aggregate for the period ending at `overviewDate`.
 *
 * `cache-and-network` is load-bearing (AUT-5782). Under Apollo's default `cache-first` this query
 * never refetched once it had resolved: there was no `pollInterval`, and the only `refetch()` was
 * wired to PayeeOverview's error-state "Try again" button. The tile therefore served the figure it
 * fetched at page load for the life of that page, while the transactions table below it -- which
 * sets `network-only` -- refetched on every mount, page turn, and sort. That asymmetry is what made
 * the scorecard look like it updated once a day against an hourly table.
 *
 * This matches `PermitStatsCards`, which already uses the same policy for the analogous stat tile,
 * and it re-renders the cached figure immediately rather than flashing a loading state the way
 * `network-only` would.
 *
 * It also polls, so a dashboard left sitting open advances on its own rather than only on
 * navigation. Polling pauses while the tab is hidden.
 */
export function useOverviewQuery(
  overviewDate: Date,
): QueryResult<GetPayeeTransactionsOverviewData, GetPayeeTransactionsOverviewVars> {
  const { curPeriodStart, curPeriodEnd, prevPeriodStart, prevPeriodEnd } = getOverviewPeriods(overviewDate);

  return useQuery<GetPayeeTransactionsOverviewData, GetPayeeTransactionsOverviewVars>(GET_PAYEE_TRANSACTIONS_OVERVIEW, {
    variables: {
      prevPeriodStartDate: formatDate(prevPeriodStart, "yyyy-MM-dd"),
      prevPeriodEndDate: formatDate(prevPeriodEnd, "yyyy-MM-dd"),
      curPeriodStartDate: formatDate(curPeriodStart, "yyyy-MM-dd"),
      curPeriodEndDate: formatDate(curPeriodEnd, "yyyy-MM-dd"),
    },
    fetchPolicy: OVERVIEW_FETCH_POLICY,
    pollInterval: OVERVIEW_POLL_INTERVAL_MS,
    // A backgrounded tab does not need to keep querying; the first tick after it is focused again
    // picks the figure back up. Without this, a dashboard parked on a second monitor overnight
    // would issue a poll every five minutes with nobody looking at it.
    skipPollAttempt: () => typeof document !== "undefined" && document.visibilityState === "hidden",
  });
}
